export default function OKCTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Oklahoma City Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how OKC travelers actually move helps us anticipate the passport urgencies that arise here. From energy conference sprints to Tinker AFB PCS orders, Central Oklahoma travel operates on a different rhythm than most U.S. markets.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {/* Will Rogers Airport */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">OKC — Will Rogers World Airport</h3>
                <p className="text-gray-400 text-xs">Southwest Oklahoma City, 10 miles from downtown</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Will Rogers World Airport (OKC) serves Central Oklahoma with nonstop service to major domestic hubs including Dallas, Denver, Chicago, Atlanta, Houston, and Las Vegas — with international connections throughout. Energy executives frequently use OKC for last-minute bookings to Houston and onward to international energy markets in Dubai, London, and Calgary. The airport's compact size means check-in can be quick, but that does not help if your passport is expired.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Many OKC energy professionals book international travel through Dallas or Houston hubs. Even if your first leg is domestic, international connections require a valid passport — check yours before booking.
              </p>
            </div>
          </div>

          {/* Energy Industry Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-oil-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Energy Industry Business Travel</h3>
                <p className="text-gray-400 text-xs">Devon Energy, Chesapeake, downtown OKC energy corridor</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Oklahoma City's downtown energy corridor is home to some of the largest oil and gas companies in North America. Deal-driven travel to Houston, Calgary, Abu Dhabi, and London happens on short notice — sometimes 72 hours or less. We regularly process urgent passport renewals for energy executives whose passports expired during COVID-era travel lulls. The combination of high travel frequency and compressed booking timelines makes OKC energy professionals one of our most time-sensitive client categories.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Energy professionals who travel internationally more than twice a year should consider a second passport to allow continuous travel while one passport is being processed. We handle second passport applications too.
              </p>
            </div>
          </div>

          {/* Tinker AFB & Military */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-shield-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Tinker AFB PCS &amp; Military Travel</h3>
                <p className="text-gray-400 text-xs">Midwest City, 8 miles east of downtown OKC</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Tinker Air Force Base is the largest military installation in Oklahoma with over 26,000 personnel. Military families receive PCS orders with 30–60 days notice, often requiring passport renewals for dependents before OCONUS assignments to Germany, Japan, South Korea, and the UK. The base passport office serves active duty but has limited hours and capacity. Our expedited service helps Tinker families meet tight timelines. We also process emergency passports for bereavement leave and emergency deployment travel.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Tinker families: check every dependent's passport expiration as soon as PCS orders arrive. Children's passports expire in 5 years, and many families discover expired passports during the move-out rush.
              </p>
            </div>
          </div>

          {/* OU Health & Medical Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-heart-pulse-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">OU Health &amp; Medical Conference Travel</h3>
                <p className="text-gray-400 text-xs">OU Health, Oklahoma Health Center, downtown OKC</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              The Oklahoma Health Center near downtown OKC is a major biomedical hub. Physicians, researchers, and medical staff frequently travel to international conferences in Geneva, London, Tokyo, and Sydney. Conference acceptance letters often arrive with 4–6 weeks of lead time — not enough for routine processing. Our expedited service ensures medical professionals can focus on their presentations while we handle their passport processing.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Medical conference travelers: keep a digital copy of your conference acceptance letter — it serves as proof of urgent travel if you need emergency processing.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-building-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Paycom Center &amp; Corporate Event Travel</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Oklahoma City's Paycom Center and Cox Convention Center host major industry conferences and corporate events that draw international business travel. Event organizers, keynote speakers, and corporate attendees flying in from abroad — or OKC executives traveling to partner events overseas — sometimes discover passport issues during event planning. We accommodate urgent corporate timelines with same-day document review and prioritized submission.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}